// GENERATED by scripts/gen.mjs from ../schema — do not edit by hand. Re-run: pnpm --filter @glyph/protocol-ts gen
import type * as Topics from './topics';

type LocalToken = Topics.LocalToken;
type UpdateStatus = Topics.UpdateStatus;

export interface AlertsAckPayload {
  key: string;
}
export interface AudioListenStartPayload {
  session_id: string;
  ttl_s?: number;
  bitrate_kbps?: number;
}
export interface AudioListenStopPayload {
  session_id?: string | null;
}
export interface AudioTestPayload {
  seconds?: number;
}
/**
 * Cloud replaces the device's accepted local-shell bearer set (also delivered in `welcome.assignment`).
 */
export interface AuthLocalTokensPayload {
  tokens?: LocalToken[];
}
export interface AuthLoginPayload {
  username: string;
  password: string;
}
export interface CaptionsInjectPayload {
  text: string;
  final?: boolean;
}
export interface CaptionsRelayPayload {
  enabled: boolean;
  /**
   * relay partials too (while a browser is subscribed)
   */
  partials?: boolean;
  /**
   * relay the audio meter (`levels`, <= 4 Hz) while a browser is subscribed
   */
  levels?: boolean;
}
export interface CloudEnrollPayload {
  url: string;
  enrol_token: string;
}
export interface CredentialsDeepgramPayload {
  key_id: string;
  api_key: string;
  expires_at: number;
  project_id?: string | null;
  tags?: string[];
  revoke_key_ids?: string[];
}
export interface CredentialsResult {
  ok: boolean;
  active_key_id: string | null;
  standby_key_id: string | null;
  expires_at: number | null;
}
export interface DataGrowResult {
  ok: boolean;
  output: string;
  free_gb: number | null;
}
/**
 * A Deepgram API key entered on the unit (System page); replaces any local key.
 */
export interface DeepgramKeySetPayload {
  api_key: string;
}
export interface DeviceLockPayload {
  locked: boolean;
  message?: string;
}
/**
 * Free-form display name, the same rule as `DeviceSettings.name` (the SSID uses its slug).
 */
export interface DeviceRenamePayload {
  name: string;
}
export interface DeviceWipePayload {
  confirm: 'WIPE';
  keep_identity?: boolean;
}
export interface DisplayModesResult {
  ok: boolean;
  outputs: Record<string, unknown>[];
}
export interface DisplayOutputsSetPayload {
  policy?: ('mirror' | 'independent') | null;
  assignments?: {
    [k: string]: string;
  } | null;
  mode_override?: string | null;
  persist?: boolean;
}
export interface DisplayOverlayPayload {
  kind?: 'none' | 'blank' | 'test_pattern' | 'badge';
  text?: string | null;
  output?: string | null;
}
/**
 * Partial DisplaySettings applied live (not persisted) for ~30 s or until next patch.
 */
export interface DisplayPreviewPayload {
  style?: Record<string, unknown>;
  sample_text?: string | null;
  ttl_s?: number;
}
export interface DriveActionPayload {
  uuid: string;
}
export interface EngineResult {
  ok: boolean;
  running: boolean;
}
export interface ExportPayload {
  /**
   * session name
   */
  name: string;
  format: 'srt' | 'vtt' | 'scc' | 'fcpxml' | 'xml' | 'edl' | 'csv' | 'txt' | 'json';
  zero?: ('session' | 'tc_hour' | 'custom') | null;
  zero_tc?: string | null;
  chars_per_line?: number | null;
}
export interface ExportResult {
  ok: boolean;
  name: string;
  format: string;
  size: number;
  download_url: string | null;
  /**
   * inline for small exports
   */
  content: string | null;
}
export interface FilesListPayload {
  drive?: string | null;
  query?: string;
  tag?: string | null;
  limit?: number;
  offset?: number;
}
export interface FilesNamePayload {
  name: string;
}
export interface FilesRenamePayload {
  name: string;
  display_name: string;
}
export interface FilesTagPayload {
  name: string;
  tags?: string[];
}
/**
 * Cloud asks the device to push a recording through /xfer (on_request upload policy).
 */
export interface FilesUploadPayload {
  name: string;
  include_audio?: boolean;
  transfer_token?: string | null;
}
export interface FilesZipPayload {
  /**
   * @minItems 1
   * @maxItems 500
   */
  names: [string, ...string[]];
}
export interface FontDeletePayload {
  file: string;
}
export interface FontsListResult {
  ok: boolean;
  fonts: Record<string, unknown>[];
}
export interface IdentityRotatePayload {
  new_key_id?: string | null;
  reason?: string;
}
export interface IdentityRotateResult {
  ok: boolean;
  key_id: string;
  public_key: string;
}
/**
 * Cloud push of a word library (coalesced if the device is offline).
 */
export interface LibraryApplyPayload {
  library_id: string;
  version: string;
  /**
   * [{term, boost}]
   */
  keyterms?: Record<string, unknown>[];
  replacements?: {
    [k: string]: string;
  };
  blocked?: string[];
  blocked_mode?: ('replace' | 'blank' | 'drop') | null;
  vulgarity_filter?: boolean | null;
  vulgarity_custom?: string[];
}
export interface LogsTailPayload {
  n?: number;
  level?: string;
  contains?: string;
}
export interface NetworkStatusPayload {
  quick?: boolean;
}
export interface OkResult {
  ok: boolean;
  message: string | null;
}
export interface OledTestPayload {
  on?: boolean;
}
export interface PartnerFollowPayload {
  follow: 'off' | 'on' | 'auto';
}
export interface PartnerPairPayload {
  peer_host: string;
  pair_token: string;
  role?: 'primary' | 'backup';
}
export interface PartnerRolePayload {
  role: 'primary' | 'backup';
}
export interface RecordNamePayload {
  name: string;
}
export interface RecordStartPayload {
  name?: string | null;
}
export interface SettingsGetPayload {
  /**
   * return real secrets (local, unlocked session only)
   */
  reveal?: boolean;
  sections?: string[] | null;
}
export interface SettingsGetResult {
  ok: boolean;
  settings: Record<string, unknown>;
}
/**
 * `{"patch": {section: {key: value}}}`. Legacy form (sections at top level) is also accepted by the agent.
 */
export interface SettingsPatchPayload {
  /**
   * partial Settings; merged then validated
   */
  patch: {
    [k: string]: Record<string, unknown>;
  };
}
export interface SettingsPatchResult {
  ok: boolean;
  changed: string[];
}
/**
 * One preset's contents, so a console can copy it to other units.
 */
export interface SettingsProfileGetResult {
  ok: boolean;
  name: string;
  settings: {
    [k: string]: Record<string, unknown>;
  };
  sections: string[];
  saved_at: number | null;
}
export interface SettingsProfilePayload {
  name: string;
}
/**
 * Import a preset: write `settings` (profile sections only; others are dropped) under `name`, applying it when `apply`.
 */
export interface SettingsProfilePutPayload {
  name: string;
  settings: {
    [k: string]: Record<string, unknown>;
  };
  apply?: boolean;
}
export interface StatusResult {
  ok: boolean;
  ts: number;
  health: Record<string, unknown>;
  engine: Record<string, unknown>;
  stats: Record<string, unknown>;
  network: Record<string, unknown>;
  record: Record<string, unknown>;
  timecode: Record<string, unknown>;
  version: Record<string, unknown>;
  system_state: string;
  setup_completed: boolean;
  auth_required: boolean;
  cloud: Record<string, unknown>;
  partner: Record<string, unknown>;
  outputs: Record<string, unknown>[];
}
export interface TimecodeOffsetPayload {
  offset_frames: number;
  persist?: boolean;
}
export interface TimecodeSourcePayload {
  source: 'off' | 'freerun' | 'system' | 'ltc' | 'atem' | 'hyperdeck';
  host?: string | null;
  framerate?: number | null;
  start?: string | null;
}
export interface UpdateApplyPayload {
  /**
   * staged version to apply; None = whatever is staged
   */
  version?: string | null;
  /**
   * apply even if engine running / recording
   */
  force?: boolean;
}
export interface UpdateCheckResult {
  ok: boolean;
  status: UpdateStatus;
  channel: string;
  newest: string | null;
  available: boolean;
  offered: boolean;
  message: string;
}
export interface UpdateOfferPayload {
  version: string;
  kind?: 'app' | 'os';
  url: string;
  size: number;
  sha256: string;
  manifest?: Record<string, unknown>;
  manifest_sig: string;
  allow_downgrade?: boolean;
  /**
   * install even if the engine is running / version checks would skip
   */
  force?: boolean;
  /**
   * manual: stage only; the operator applies with update.apply (Install staged)
   */
  policy?: 'immediate' | 'skip_if_engine_running' | 'manual';
  deadline?: number | null;
  rollout_id?: string | null;
  transfer_token?: string | null;
}
export interface UpdateStagePayload {
  /**
   * signed bundle on the device (USB under /media/glyph or a downloads path); <stem>.manifest.json + .manifest.sig next to it
   */
  path: string;
  allow_downgrade?: boolean;
  /**
   * ignore the installed release's security_floor
   */
  force?: boolean;
}
export interface UpdateStatusResult {
  ok: boolean;
  status: UpdateStatus;
  /**
   * privd update.status: current/previous/staged releases with good/trial/broken flags
   */
  releases: Record<string, unknown> | null;
}
export interface WifiConnectPayload {
  ssid: string;
  password?: string;
  username?: string;
  hidden?: boolean;
}
export interface WifiForgetPayload {
  ssid: string;
}
export interface WordLibGetResult {
  ok: boolean;
  library: Record<string, unknown>;
  keyterm_budget: Record<string, unknown>;
}
export interface WordLibSetPayload {
  library_id?: string;
  version?: string | null;
  /**
   * [{term, boost}]
   */
  keyterms?: Record<string, unknown>[];
  replacements?: {
    [k: string]: string;
  };
  blocked?: string[];
  blocked_mode?: ('replace' | 'blank' | 'drop') | null;
  vulgarity_filter?: boolean | null;
  vulgarity_custom?: string[];
}

// ---- registry (from commands.json) -----------------------------------------------------------------
export type Empty = Record<string, never>;
/** Payload for actions whose model is not published yet: any JSON object. */
export type UntypedPayload = Record<string, unknown>;
/** Result for actions whose result model is not published yet (the `data` of CmdResult). */
export type UntypedResult = Record<string, any>;

export interface Commands {
  'alerts.ack': { payload: AlertsAckPayload; result: UntypedResult };
  'alerts.list': { payload: UntypedPayload; result: UntypedResult };
  'audio.devices': { payload: UntypedPayload; result: UntypedResult };
  'audio.listen.start': { payload: AudioListenStartPayload; result: UntypedResult };
  'audio.listen.stop': { payload: AudioListenStopPayload; result: UntypedResult };
  'audio.test': { payload: AudioTestPayload; result: UntypedResult };
  'auth.local_tokens': { payload: AuthLocalTokensPayload; result: UntypedResult };
  'auth.login': { payload: AuthLoginPayload; result: UntypedResult };
  'auth.logout': { payload: UntypedPayload; result: UntypedResult };
  'auth.status': { payload: UntypedPayload; result: UntypedResult };
  'auth.unlock': { payload: UntypedPayload; result: UntypedResult };
  'captions.clear': { payload: UntypedPayload; result: UntypedResult };
  'captions.inject': { payload: CaptionsInjectPayload; result: UntypedResult };
  'captions.relay': { payload: CaptionsRelayPayload; result: UntypedResult };
  'captions.snapshot': { payload: UntypedPayload; result: UntypedResult };
  'cloud.enroll': { payload: CloudEnrollPayload; result: UntypedResult };
  'cloud.reconnect': { payload: UntypedPayload; result: UntypedResult };
  'cloud.status': { payload: UntypedPayload; result: UntypedResult };
  'commands.list': { payload: UntypedPayload; result: UntypedResult };
  'credentials.deepgram': { payload: CredentialsDeepgramPayload; result: CredentialsResult };
  'credentials.deepgram.set': { payload: DeepgramKeySetPayload; result: UntypedResult };
  'deepgram.reconnect': { payload: UntypedPayload; result: UntypedResult };
  'deepgram.status': { payload: UntypedPayload; result: UntypedResult };
  'device.lock': { payload: DeviceLockPayload; result: UntypedResult };
  'device.rename': { payload: DeviceRenamePayload; result: UntypedResult };
  'device.wipe': { payload: DeviceWipePayload; result: UntypedResult };
  'display.outputs.get': { payload: UntypedPayload; result: DisplayModesResult };
  'display.outputs.set': { payload: DisplayOutputsSetPayload; result: UntypedResult };
  'display.overlay': { payload: DisplayOverlayPayload; result: UntypedResult };
  'display.preview': { payload: DisplayPreviewPayload; result: UntypedResult };
  'engine.restart': { payload: UntypedPayload; result: EngineResult };
  'engine.start': { payload: UntypedPayload; result: EngineResult };
  'engine.stop': { payload: UntypedPayload; result: EngineResult };
  'export.formats': { payload: UntypedPayload; result: UntypedResult };
  'export.run': { payload: ExportPayload; result: ExportResult };
  'files.delete': { payload: FilesNamePayload; result: UntypedResult };
  'files.list': { payload: FilesListPayload; result: UntypedResult };
  'files.rename': { payload: FilesRenamePayload; result: UntypedResult };
  'files.search': { payload: FilesListPayload; result: UntypedResult };
  'files.sync': { payload: UntypedPayload; result: UntypedResult };
  'files.tag': { payload: FilesTagPayload; result: UntypedResult };
  'files.upload': { payload: FilesUploadPayload; result: UntypedResult };
  'files.zip': { payload: FilesZipPayload; result: UntypedResult };
  'fonts.delete': { payload: FontDeletePayload; result: UntypedResult };
  'fonts.list': { payload: UntypedPayload; result: FontsListResult };
  'fonts.reload': { payload: UntypedPayload; result: UntypedResult };
  'health.get': { payload: UntypedPayload; result: UntypedResult };
  'identity.rotate': { payload: IdentityRotatePayload; result: IdentityRotateResult };
  'library.apply': { payload: LibraryApplyPayload; result: UntypedResult };
  'logs.tail': { payload: LogsTailPayload; result: UntypedResult };
  'network.ap.apply': { payload: UntypedPayload; result: UntypedResult };
  'network.eth.apply': { payload: UntypedPayload; result: UntypedResult };
  'network.lte.connect': { payload: UntypedPayload; result: UntypedResult };
  'network.lte.disconnect': { payload: UntypedPayload; result: UntypedResult };
  'network.lte.status': { payload: UntypedPayload; result: UntypedResult };
  'network.status': { payload: NetworkStatusPayload; result: UntypedResult };
  'network.wifi.connect': { payload: WifiConnectPayload; result: UntypedResult };
  'network.wifi.disconnect': { payload: UntypedPayload; result: UntypedResult };
  'network.wifi.forget': { payload: WifiForgetPayload; result: UntypedResult };
  'network.wifi.saved': { payload: UntypedPayload; result: UntypedResult };
  'network.wifi.scan': { payload: UntypedPayload; result: UntypedResult };
  'oled.test': { payload: OledTestPayload; result: UntypedResult };
  'partner.follow': { payload: PartnerFollowPayload; result: UntypedResult };
  'partner.pair': { payload: PartnerPairPayload; result: UntypedResult };
  'partner.role': { payload: PartnerRolePayload; result: UntypedResult };
  'partner.status': { payload: UntypedPayload; result: UntypedResult };
  'partner.unpair': { payload: UntypedPayload; result: UntypedResult };
  'record.delete': { payload: RecordNamePayload; result: UntypedResult };
  'record.drive.eject': { payload: DriveActionPayload; result: UntypedResult };
  'record.drive.mount': { payload: DriveActionPayload; result: UntypedResult };
  'record.drives': { payload: UntypedPayload; result: UntypedResult };
  'record.sessions': { payload: UntypedPayload; result: UntypedResult };
  'record.start': { payload: RecordStartPayload; result: UntypedResult };
  'record.status': { payload: UntypedPayload; result: UntypedResult };
  'record.stop': { payload: UntypedPayload; result: UntypedResult };
  'settings.get': { payload: SettingsGetPayload; result: SettingsGetResult };
  'settings.patch': { payload: SettingsPatchPayload; result: SettingsPatchResult };
  'settings.profiles.delete': { payload: SettingsProfilePayload; result: UntypedResult };
  'settings.profiles.get': { payload: SettingsProfilePayload; result: SettingsProfileGetResult };
  'settings.profiles.list': { payload: UntypedPayload; result: UntypedResult };
  'settings.profiles.load': { payload: SettingsProfilePayload; result: UntypedResult };
  'settings.profiles.put': { payload: SettingsProfilePutPayload; result: SettingsPatchResult };
  'settings.profiles.save': { payload: SettingsProfilePayload; result: UntypedResult };
  'setup.complete': { payload: UntypedPayload; result: UntypedResult };
  'stats.get': { payload: UntypedPayload; result: UntypedResult };
  'status.get': { payload: UntypedPayload; result: StatusResult };
  'system.data.grow': { payload: UntypedPayload; result: DataGrowResult };
  'system.identify': { payload: UntypedPayload; result: UntypedResult };
  'system.info': { payload: UntypedPayload; result: UntypedResult };
  'system.reboot': { payload: UntypedPayload; result: OkResult };
  'system.restart': { payload: UntypedPayload; result: OkResult };
  'system.selftest': { payload: UntypedPayload; result: UntypedResult };
  'system.shutdown': { payload: UntypedPayload; result: OkResult };
  'timecode.now': { payload: UntypedPayload; result: UntypedResult };
  'timecode.offset': { payload: TimecodeOffsetPayload; result: UntypedResult };
  'timecode.source': { payload: TimecodeSourcePayload; result: UntypedResult };
  'timecode.status': { payload: UntypedPayload; result: UntypedResult };
  'update.apply': { payload: UpdateApplyPayload; result: UpdateStatusResult };
  'update.cancel': { payload: UntypedPayload; result: UpdateStatusResult };
  'update.check': { payload: UntypedPayload; result: UpdateCheckResult };
  'update.offer': { payload: UpdateOfferPayload; result: UpdateStatusResult };
  'update.rollback': { payload: UntypedPayload; result: UpdateStatusResult };
  'update.slots': { payload: UntypedPayload; result: UntypedResult };
  'update.stage': { payload: UpdateStagePayload; result: UpdateStatusResult };
  'update.status': { payload: UntypedPayload; result: UpdateStatusResult };
  'version.get': { payload: UntypedPayload; result: UntypedResult };
  'wordlib.get': { payload: UntypedPayload; result: WordLibGetResult };
  'wordlib.set': { payload: WordLibSetPayload; result: UntypedResult };
}
export type CommandAction = keyof Commands;
export type CommandPayload<A extends CommandAction> = Commands[A]['payload'];
export type CommandResult<A extends CommandAction> = Commands[A]['result'];

export type Role = 'viewer' | 'operator' | 'admin' | 'owner' | 'platform';
export const ROLES: readonly Role[] = ["viewer","operator","admin","owner","platform"];
export type Capability = 'alerts.ack' | 'audio.listen' | 'auth.write' | 'captions.read' | 'captions.write' | 'credentials.broker' | 'device.admin' | 'device.provision' | 'device.read' | 'device.rename' | 'display.write' | 'engine.control' | 'export.run' | 'files.download' | 'files.read' | 'files.write' | 'fonts.write' | 'library.write' | 'logs.read' | 'network.write' | 'partner.write' | 'record.control' | 'settings.admin' | 'settings.write' | 'system.identify' | 'system.reboot' | 'system.shutdown' | 'timecode.write' | 'update.apply';
/** Minimum role per capability. */
export const CAPABILITY_ROLE: Record<Capability, Role> = {
  "alerts.ack": "operator",
  "audio.listen": "operator",
  "auth.write": "owner",
  "captions.read": "viewer",
  "captions.write": "operator",
  "credentials.broker": "platform",
  "device.admin": "admin",
  "device.provision": "platform",
  "device.read": "viewer",
  "device.rename": "operator",
  "display.write": "operator",
  "engine.control": "operator",
  "export.run": "viewer",
  "files.download": "viewer",
  "files.read": "viewer",
  "files.write": "operator",
  "fonts.write": "admin",
  "library.write": "operator",
  "logs.read": "viewer",
  "network.write": "admin",
  "partner.write": "admin",
  "record.control": "operator",
  "settings.admin": "admin",
  "settings.write": "operator",
  "system.identify": "operator",
  "system.reboot": "operator",
  "system.shutdown": "admin",
  "timecode.write": "operator",
  "update.apply": "admin"
};
export const DEFAULT_TIMEOUT_MS = 15000;

export interface CommandMeta {
  capability: Capability;
  minRole: Role;
  queueable: boolean;
  timeoutMs: number;
  /** false = allowed before PIN unlock on the local shell */
  auth: boolean;
  cloudOnly: boolean;
  description: string;
}
export const COMMAND_META: Record<CommandAction, CommandMeta> = {
  'alerts.ack': { capability: 'alerts.ack', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'alerts.list': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'audio.devices': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'audio.listen.start': { capability: 'audio.listen', minRole: 'operator', queueable: false, timeoutMs: 10000, auth: true, cloudOnly: false, description: "Start Opus monitor stream to the cloud (auto-stop after ttl)" },
  'audio.listen.stop': { capability: 'audio.listen', minRole: 'operator', queueable: false, timeoutMs: 10000, auth: true, cloudOnly: false, description: "" },
  'audio.test': { capability: 'engine.control', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Short capture + level report" },
  'auth.local_tokens': { capability: 'device.provision', minRole: 'platform', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: true, description: "Cloud pushes the accepted local-shell bearer hashes (minted when a user opens the device page)" },
  'auth.login': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Local sign-in: username (fdgadmin | tech username) + password -> session token + role" },
  'auth.logout': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'auth.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'auth.unlock': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Admin login with a PIN/password (pre-account shell)" },
  'captions.clear': { capability: 'captions.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'captions.inject': { capability: 'captions.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Manually push a caption line" },
  'captions.relay': { capability: 'captions.write', minRole: 'operator', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Cloud toggles caption relay (partials while a browser is subscribed)" },
  'captions.snapshot': { capability: 'captions.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Finals ring + current partial" },
  'cloud.enroll': { capability: 'settings.admin', minRole: 'admin', queueable: false, timeoutMs: 60000, auth: true, cloudOnly: false, description: "" },
  'cloud.reconnect': { capability: 'settings.admin', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'cloud.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'commands.list': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Advertised command list" },
  'credentials.deepgram': { capability: 'credentials.broker', minRole: 'platform', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: true, description: "Cloud pushes a brokered Deepgram key (primary/standby)" },
  'credentials.deepgram.set': { capability: 'settings.admin', minRole: 'admin', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Store a Deepgram API key (unit System page, or pushed from the console to all/selected units)" },
  'deepgram.reconnect': { capability: 'engine.control', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Force a Deepgram reconnect with ring replay" },
  'deepgram.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'device.lock': { capability: 'device.admin', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Lock the local UI (cloud-controlled)" },
  'device.rename': { capability: 'device.rename', minRole: 'operator', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Set the free-form device name (OLED, fleet, mDNS; the hotspot SSID uses its slug)" },
  'device.wipe': { capability: 'device.provision', minRole: 'platform', queueable: false, timeoutMs: 120000, auth: true, cloudOnly: false, description: "Factory reset: settings, recordings, credentials" },
  'display.outputs.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Connected outputs and supported modes" },
  'display.outputs.set': { capability: 'display.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Set output policy / connector->layout assignments / mode override" },
  'display.overlay': { capability: 'display.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Blank / test pattern / badge overlay" },
  'display.preview': { capability: 'display.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Apply a style live without persisting (reverts after ttl)" },
  'engine.restart': { capability: 'engine.control', minRole: 'operator', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'engine.start': { capability: 'engine.control', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'engine.stop': { capability: 'engine.control', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'export.formats': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'export.run': { capability: 'export.run', minRole: 'viewer', queueable: false, timeoutMs: 120000, auth: true, cloudOnly: false, description: "" },
  'files.delete': { capability: 'files.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'files.list': { capability: 'files.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'files.rename': { capability: 'files.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Set display_name (on-disk name unchanged)" },
  'files.search': { capability: 'files.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'files.sync': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Re-send the file_sync manifest" },
  'files.tag': { capability: 'files.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'files.upload': { capability: 'files.write', minRole: 'operator', queueable: false, timeoutMs: 60000, auth: true, cloudOnly: false, description: "Push a recording to the cloud via /xfer (resumable)" },
  'files.zip': { capability: 'files.download', minRole: 'viewer', queueable: false, timeoutMs: 120000, auth: true, cloudOnly: false, description: "Build a zip; result carries a download token" },
  'fonts.delete': { capability: 'fonts.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'fonts.list': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'fonts.reload': { capability: 'fonts.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Re-index bundled + uploaded fonts" },
  'health.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Component health snapshot" },
  'identity.rotate': { capability: 'device.provision', minRole: 'platform', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: true, description: "" },
  'library.apply': { capability: 'library.write', minRole: 'operator', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Cloud push of an event word library" },
  'logs.tail': { capability: 'logs.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Tail the in-memory log ring" },
  'network.ap.apply': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "Re-apply hotspot config" },
  'network.eth.apply': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'network.lte.connect': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 60000, auth: true, cloudOnly: false, description: "" },
  'network.lte.disconnect': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'network.lte.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'network.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'network.wifi.connect': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 60000, auth: true, cloudOnly: false, description: "" },
  'network.wifi.disconnect': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'network.wifi.forget': { capability: 'network.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'network.wifi.saved': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'network.wifi.scan': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'oled.test': { capability: 'system.identify', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'partner.follow': { capability: 'partner.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'partner.pair': { capability: 'partner.write', minRole: 'admin', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'partner.role': { capability: 'partner.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'partner.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'partner.unpair': { capability: 'partner.write', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'record.delete': { capability: 'files.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'record.drive.eject': { capability: 'record.control', minRole: 'operator', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'record.drive.mount': { capability: 'record.control', minRole: 'operator', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'record.drives': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'record.sessions': { capability: 'files.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'record.start': { capability: 'record.control', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'record.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'record.stop': { capability: 'record.control', minRole: 'operator', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "" },
  'settings.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'settings.patch': { capability: 'settings.write', minRole: 'operator', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Deep-merge a partial Settings; per-section capability via SECTION_CAPABILITY" },
  'settings.profiles.delete': { capability: 'settings.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'settings.profiles.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "One preset's settings (so the console can push it to other units)" },
  'settings.profiles.list': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'settings.profiles.load': { capability: 'settings.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'settings.profiles.put': { capability: 'settings.write', minRole: 'operator', queueable: true, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Store a preset sent from another unit / the console; apply it when `apply`" },
  'settings.profiles.save': { capability: 'settings.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'setup.complete': { capability: 'settings.admin', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Mark the setup wizard done" },
  'stats.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "System stats (cpu/mem/temp/net/audio)" },
  'status.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Composite status for the overview page" },
  'system.data.grow': { capability: 'device.admin', minRole: 'admin', queueable: false, timeoutMs: 180000, auth: true, cloudOnly: false, description: "Grow the data filesystem to its volume (resize2fs, online); for a unit whose first-boot grow did not finish" },
  'system.identify': { capability: 'system.identify', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Flash OLED + badge overlay to find the box" },
  'system.info': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Hostname, OS, model, disk, memory, throttle flags" },
  'system.reboot': { capability: 'system.reboot', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'system.restart': { capability: 'system.reboot', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Restart agent services" },
  'system.selftest': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 60000, auth: true, cloudOnly: false, description: "" },
  'system.shutdown': { capability: 'system.shutdown', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'timecode.now': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'timecode.offset': { capability: 'timecode.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Manual frame offset applied last" },
  'timecode.source': { capability: 'timecode.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'timecode.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'update.apply': { capability: 'update.apply', minRole: 'admin', queueable: false, timeoutMs: 120000, auth: true, cloudOnly: false, description: "" },
  'update.cancel': { capability: 'update.apply', minRole: 'admin', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'update.check': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 30000, auth: true, cloudOnly: false, description: "Ask the cloud for the newest published release in the unit's channel; a newer one is staged (policy manual)" },
  'update.offer': { capability: 'update.apply', minRole: 'admin', queueable: true, timeoutMs: 30000, auth: true, cloudOnly: false, description: "Download + verify + stage a signed release; apply per policy" },
  'update.rollback': { capability: 'update.apply', minRole: 'admin', queueable: false, timeoutMs: 120000, auth: true, cloudOnly: false, description: "" },
  'update.slots': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "OS A/B slot state" },
  'update.stage': { capability: 'update.apply', minRole: 'admin', queueable: false, timeoutMs: 600000, auth: true, cloudOnly: false, description: "Install from file: verify a signed bundle already on the device and stage it (no switch)" },
  'update.status': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "" },
  'version.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: false, cloudOnly: false, description: "Installed/previous/staged versions" },
  'wordlib.get': { capability: 'device.read', minRole: 'viewer', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "" },
  'wordlib.set': { capability: 'library.write', minRole: 'operator', queueable: false, timeoutMs: 15000, auth: true, cloudOnly: false, description: "Local edit of the active library" },
};
export const COMMAND_ACTIONS = Object.keys(COMMAND_META) as CommandAction[];
export const QUEUEABLE_ACTIONS: readonly CommandAction[] = COMMAND_ACTIONS.filter((a) => COMMAND_META[a].queueable);
const ROLE_RANK: Record<Role, number> = Object.fromEntries(ROLES.map((r, i) => [r, i])) as Record<Role, number>;
/** True when `role` meets the action's minimum role (platform outranks owner). */
export const roleCan = (role: Role, action: CommandAction): boolean => ROLE_RANK[role] >= ROLE_RANK[COMMAND_META[action].minRole];
